import { useEffect, useState, useRef } from 'react';
import { nodeDataAutoSaveDynamic } from '../util/autosave/nodedata_autosave';
import React from 'react';

interface InputSize {
    width: string;
    height: string;
}

interface UseInputSizeTrackingOptions {
    nodeId: string;
    inputSize?: InputSize;
    collapsed?: boolean;
    edges: any[];
    flowId?: string;
    setNodeState: any;
    setSaving: any;
    setSchema: any;
    sizeKey?: string;
    textareaRef?: React.RefObject<HTMLTextAreaElement>;
    textareaName?: string;
}

export const useInputSizeTracking = ({
    nodeId,
    inputSize: savedInputSize,
    collapsed = false,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
    sizeKey = 'inputSize',
    textareaRef,
    textareaName,
}: UseInputSizeTrackingOptions): InputSize | undefined => {
    const [inputSize, setInputSize] = useState(savedInputSize);
    const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isResizingRef = useRef(false);

    useEffect(() => {
        if (savedInputSize) {
            setInputSize(savedInputSize);
        }
    }, [savedInputSize]);

    useEffect(() => {
        if (collapsed) return;

        const findTextarea = (): HTMLTextAreaElement | null => {
            if (textareaRef?.current) {
                return textareaRef.current;
            }

            if (nodeId) {
                const selector = textareaName
                    ? `[data-id="${nodeId}"] textarea[name="${textareaName}"]`
                    : `[data-id="${nodeId}"] textarea`;
                return document.querySelector(selector) as HTMLTextAreaElement;
            }

            return null;
        };

        const setupObserver = (element: HTMLTextAreaElement) => {
            if (savedInputSize) {
                if (savedInputSize.width) {
                    element.style.width = savedInputSize.width;
                }
                if (savedInputSize.height) {
                    element.style.height = savedInputSize.height;
                }
            }

            const resizeObserver = new ResizeObserver(() => {
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }

                isResizingRef.current = true;

                const width = element.style.width || `${element.offsetWidth}px`;
                const height = element.style.height || `${element.offsetHeight}px`;
                setInputSize({ width, height });

                resizeTimeoutRef.current = setTimeout(() => {
                    if (isResizingRef.current) {
                        const finalWidth = element.style.width || `${element.offsetWidth}px`;
                        const finalHeight = element.style.height || `${element.offsetHeight}px`;
                        const finalSize = { width: finalWidth, height: finalHeight };

                        if (
                            finalSize.width !== savedInputSize?.width ||
                            finalSize.height !== savedInputSize?.height
                        ) {
                            nodeDataAutoSaveDynamic({
                                newEdges: edges,
                                setNodeState,
                                id: nodeId,
                                setSaving,
                                flowId,
                                objectData: { [sizeKey]: finalSize },
                                setSchema,
                            });
                        }

                        isResizingRef.current = false;
                    }
                }, 500);
            });

            resizeObserver.observe(element);
            return resizeObserver;
        };

        let textarea = findTextarea();
        let resizeObserver: ResizeObserver | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        if (!textarea) {
            timeoutId = setTimeout(() => {
                textarea = findTextarea();
                if (textarea) {
                    resizeObserver = setupObserver(textarea);
                }
            }, 100);
        } else {
            resizeObserver = setupObserver(textarea);
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [nodeId, collapsed, textareaRef, textareaName, savedInputSize, edges, flowId, setNodeState, setSaving, setSchema, sizeKey]);

    return inputSize || savedInputSize;
};

